import { z } from 'zod';

/**
 * Zod schemas for BP-25 §5's responses (ARCHITECTURE §3 Validation).
 *
 * Hand-written, for the reason `features/reports/schemas.ts` gives:
 * `lib/api/schema.d.ts` is regenerated in CI, and a screen compiled
 * against it would track whatever was last exported rather than the
 * contract this feature was written to.
 *
 * **Money is integer paise** and `rupees()` in `components` is the one
 * place that divides, so no screen does its own arithmetic.
 *
 * **A waitlist entry carries no name and no number.** The API does not
 * send them (§9) and this is where that would first show up as a
 * missing field, so it is written down rather than discovered.
 */

/** BP-25 §3's `booking_deposit.status`, in the order a deposit walks it. */
export const depositStatuses = ['requested', 'paid', 'applied', 'forfeited', 'cancelled'] as const;
export type DepositStatus = (typeof depositStatuses)[number];

/** BP-25 §3's `waitlist_entry.status`. */
export const waitlistStatuses = ['waiting', 'offered', 'booked', 'expired', 'cancelled'] as const;
export type WaitlistStatus = (typeof waitlistStatuses)[number];

/** Google's three feeds (§7). */
export const feedKinds = ['merchant', 'service', 'availability'] as const;
export type FeedKind = (typeof feedKinds)[number];

/** Rule 10's three frequencies, and nothing else. */
export const frequencies = ['DAILY', 'WEEKLY', 'MONTHLY'] as const;
export type Frequency = (typeof frequencies)[number];

/** RFC 5545's two-letter weekdays, Monday first. */
export const weekdays = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'] as const;
export type Weekday = (typeof weekdays)[number];

export const depositSchema = z.object({
  id: z.string(),
  booking_id: z.string(),
  intent_id: z.string().nullable(),
  amount_paise: z.number(),
  applied_paise: z.number(),
  status: z.enum(depositStatuses),
  paid_at: z.string().nullable(),
  applied_payment_id: z.string().nullable(),
  forfeited_at: z.string().nullable(),
  forfeit_reason: z.string().nullable(),
  created_at: z.string(),
});
export type Deposit = z.infer<typeof depositSchema>;

/** `GET /bookings/{id}/deposit` answers `null` when there is none. */
export const depositOrNullSchema = depositSchema.nullable();

export const waitlistEntrySchema = z.object({
  id: z.string(),
  client_id: z.string(),
  outlet_id: z.string(),
  service_ids: z.array(z.string()),
  window_from: z.string(),
  window_to: z.string(),
  status: z.enum(waitlistStatuses),
  offered_at: z.string().nullable(),
  offer_expires_at: z.string().nullable(),
  offer_slot_at: z.string().nullable(),
  skip_reason: z.string().nullable(),
  created_at: z.string(),
});
export type WaitlistEntry = z.infer<typeof waitlistEntrySchema>;

export const waitlistPageSchema = z.object({ items: z.array(waitlistEntrySchema) });
export type WaitlistPage = z.infer<typeof waitlistPageSchema>;

export const recurrenceSchema = z.object({
  id: z.string(),
  client_id: z.string(),
  outlet_id: z.string(),
  rrule: z.string(),
  status: z.enum(['active', 'ended']),
  until: z.string().nullable(),
  generated_until: z.string(),
  created_at: z.string(),
  frequency: z.enum(frequencies),
  interval: z.number(),
  byday: z.array(z.enum(weekdays)),
  count: z.number().nullable(),
});
export type Recurrence = z.infer<typeof recurrenceSchema>;

export const feedRunSchema = z.object({
  id: z.string(),
  kind: z.enum(feedKinds),
  status: z.enum(['queued', 'done', 'failed']),
  generated_at: z.string(),
  file_key: z.string().nullable(),
  row_count: z.number(),
  error: z.string().nullable(),
});
export type FeedRun = z.infer<typeof feedRunSchema>;
export const feedRunListSchema = z.array(feedRunSchema);

export const policySchema = z.object({
  deposit_paise: z.number(),
  deposit_min_total_paise: z.number(),
  forfeit_on_no_show: z.boolean(),
  waitlist_hold_min: z.number(),
  recurrence_horizon_days: z.number(),
  writable: z.boolean(),
});
export type PolicyOut = z.infer<typeof policySchema>;

/** The five numbers a PUT carries — `writable` is the server's, not ours. */
export type Policy = Omit<PolicyOut, 'writable'>;

/** `POST /waitlist` and `POST /me/waitlist`. */
export interface WaitlistDraft {
  service_ids: string[];
  window_from: string;
  window_to: string;
  client_id?: string;
}

/** The three controls §6 edits a repeat with. Never a raw `RRULE`. */
export interface RuleControls {
  frequency: Frequency;
  interval: number;
  byday: Weekday[];
  count: number | null;
  until: string | null;
}
